import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Qué hago si nada funciona para mi depresión?",
    answer:
      "Si ha probado varios antidepresivos en dosis y duración adecuadas sin éxito, puede tener depresión resistente al tratamiento. Las opciones incluyen ajustes de medicación, TMS, SAINT® TMS acelerada y SPRAVATO®. Una evaluación psiquiátrica puede identificar los siguientes pasos.",
  },
  {
    question: "¿Es normal que nada funcione?",
    answer:
      "Aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor no alcanzan la remisión tras dos pasos de tratamiento. Es común y tratable. Existen opciones más allá de los antidepresivos estándar.",
  },
];

export const esNothingWorksForDepression = makeEsArticle({
  slug: "nothing-works-for-my-depression",
  title: "Nada funciona para mi depresión: ¿y ahora qué? | Neuro Wellness",
  description:
    "Qué hacer cuando nada ha funcionado para su depresión: depresión resistente al tratamiento, TMS, SAINT® y SPRAVATO® como opciones más allá de los antidepresivos.",
  h1: "Nada funciona para mi depresión: ¿y ahora qué?",
  breadcrumbLabel: "Nada funciona para mi depresión",
  shortAnswer:
    "Si ha probado varios antidepresivos en dosis y duración adecuadas sin alivio, puede tener depresión resistente al tratamiento, que afecta a aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor. No es el final del camino: existen opciones más allá de los antidepresivos estándar, como TMS, SAINT® TMS acelerada y SPRAVATO®. Una evaluación psiquiátrica puede identificar los siguientes pasos.",
  keyTakeaways: [
    "La depresión resistente al tratamiento afecta a ~1/3 de los tratados.",
    "No es el final del camino.",
    "TMS, SAINT® y SPRAVATO® son opciones más allá de los antidepresivos.",
    "Una evaluación psiquiátrica identifica los siguientes pasos.",
  ],
  sections: [
    {
      heading: "No está solo",
      paragraphs: [
        "Aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor no alcanzan la remisión tras dos pasos de tratamiento, según el estudio STAR*D. Es común y tratable.",
      ],
    },
    {
      heading: "Opciones más allá de los antidepresivos",
      paragraphs: [
        "La TMS está autorizada por la FDA para la depresión que no ha respondido a medicación. SAINT® es un protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177). SPRAVATO® está aprobado por la FDA para la depresión resistente al tratamiento. Actúan por mecanismos distintos a los antidepresivos orales.",
      ],
    },
    {
      heading: "El siguiente paso",
      paragraphs: [
        "Una evaluación psiquiátrica puede identificar por qué los tratamientos anteriores no funcionaron y recomendar los siguientes pasos. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esLostHope = makeEsArticle({
  slug: "lost-hope-after-depression-treatments",
  title:
    "Perdí la esperanza tras los tratamientos para la depresión | Neuro Wellness",
  description:
    "Si ha perdido la esperanza tras varios tratamientos para la depresión, existen opciones más allá de los antidepresivos: TMS, SAINT® y SPRAVATO®.",
  h1: "Perdí la esperanza tras los tratamientos para la depresión",
  breadcrumbLabel: "Perdí la esperanza",
  shortAnswer:
    "Perder la esperanza tras varios tratamientos es comprensible y común. La depresión resistente al tratamiento afecta a aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor. Existen opciones más allá de los antidepresivos estándar —TMS, SAINT® TMS acelerada y SPRAVATO®— que actúan por mecanismos distintos. Una evaluación psiquiátrica puede identificar los siguientes pasos.",
  keyTakeaways: [
    "Perder la esperanza es común y comprensible.",
    "La depresión resistente al tratamiento afecta a ~1/3 de los tratados.",
    "Existen opciones más allá de los antidepresivos.",
    "Una evaluación psiquiátrica identifica los siguientes pasos.",
  ],
  sections: [
    {
      heading: "No es el final",
      paragraphs: [
        "Si ha probado varios antidepresivos sin alivio, puede tener depresión resistente al tratamiento. Aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor no alcanzan la remisión tras dos pasos de tratamiento.",
      ],
    },
    {
      heading: "Opciones que no ha probado",
      paragraphs: [
        "La TMS, SAINT® TMS acelerada y SPRAVATO® actúan por mecanismos distintos a los antidepresivos orales. SAINT® está autorizado por la FDA (K220177) para adultos cuya depresión no ha mejorado con antidepresivos. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esDepressionKeepsComingBack = makeEsArticle({
  slug: "why-does-depression-keep-coming-back",
  title: "¿Por qué la depresión sigue volviendo? | Neuro Wellness",
  description:
    "Por qué la depresión puede reaparecer tras el tratamiento y qué opciones existen, incluida TMS y SAINT® para la depresión recurrente.",
  h1: "¿Por qué la depresión sigue volviendo?",
  breadcrumbLabel: "La depresión sigue volviendo",
  shortAnswer:
    "La depresión puede reaparecer por varios motivos: tratamiento interrumpido demasiado pronto, dosis inadecuada, depresión resistente al tratamiento o condiciones subyacentes no tratadas. Si la depresión sigue volviendo a pesar del tratamiento, una evaluación psiquiátrica puede identificar la causa. Las opciones incluyen ajustes de medicación, TMS, SAINT® TMS acelerada y SPRAVATO®.",
  keyTakeaways: [
    "La depresión puede reaparecer por tratamiento interrumpido o dosis inadecuada.",
    "La depresión resistente al tratamiento puede causar recurrencias.",
    "Una evaluación psiquiátrica identifica la causa.",
    "TMS, SAINT® y SPRAVATO® son opciones.",
  ],
  sections: [
    {
      heading: "Por qué vuelve",
      paragraphs: [
        "La depresión puede reaparecer si el tratamiento se interrumpió demasiado pronto, la dosis fue inadecuada, hubo un error diagnóstico o la depresión es resistente al tratamiento. También puede reaparecer por condiciones comórbidas no tratadas.",
      ],
    },
    {
      heading: "Qué hacer",
      paragraphs: [
        "Hable con su médico. Las opciones incluyen ajustar la medicación, considerar tratamientos sin medicación como TMS o SAINT® (autorizado por la FDA, K220177), o SPRAVATO®. Una evaluación psiquiátrica puede identificar la causa. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esDepressedForYears = makeEsArticle({
  slug: "depressed-for-years-what-else-can-i-try",
  title: "Deprimido durante años: ¿qué más puedo probar? | Neuro Wellness",
  description:
    "Si ha estado deprimido durante años a pesar del tratamiento, existen opciones más allá de los antidepresivos: TMS, SAINT® y SPRAVATO®.",
  h1: "Deprimido durante años: ¿qué más puedo probar?",
  breadcrumbLabel: "Deprimido durante años",
  shortAnswer:
    "Si ha estado deprimido durante años a pesar del tratamiento, puede tener depresión resistente al tratamiento, que afecta a aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor. Existen opciones más allá de los antidepresivos estándar —TMS, SAINT® TMS acelerada y SPRAVATO®— que actúan por mecanismos distintos. Una evaluación psiquiátrica puede identificar los siguientes pasos.",
  keyTakeaways: [
    "La depresión de años puede ser resistente al tratamiento.",
    "Afecta a ~1/3 de los tratados.",
    "TMS, SAINT® y SPRAVATO® son opciones más allá de los antidepresivos.",
    "Una evaluación psiquiátrica identifica los siguientes pasos.",
  ],
  sections: [
    {
      heading: "La depresión de larga duración",
      paragraphs: [
        "La depresión que dura años a pesar del tratamiento puede ser resistente al tratamiento. Aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor no alcanzan la remisión tras dos pasos de tratamiento.",
      ],
    },
    {
      heading: "Opciones que no ha probado",
      paragraphs: [
        "La TMS, SAINT® TMS acelerada y SPRAVATO® actúan por mecanismos distintos a los antidepresivos orales. SAINT® está autorizado por la FDA (K220177) para adultos cuya depresión no ha mejorado con antidepresivos. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esDontWantToChangeAntidepressants = makeEsArticle({
  slug: "dont-want-to-keep-changing-antidepressants",
  title: "No quiero seguir cambiando de antidepresivo | Neuro Wellness",
  description:
    "Si no quiere seguir probando más antidepresivos, existen opciones sin medicación como TMS y SAINT® para la depresión resistente al tratamiento.",
  h1: "No quiero seguir cambiando de antidepresivo",
  breadcrumbLabel: "No más cambios de antidepresivo",
  shortAnswer:
    "Si ha probado varios antidepresivos sin alivio y no quiere seguir cambiando de medicación, existen opciones sin medicación. La TMS está autorizada por la FDA para la depresión que no ha respondido a medicación. SAINT® es un protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177). SPRAVATO® está aprobado por la FDA para la depresión resistente al tratamiento. Actúan por mecanismos distintos a los antidepresivos orales.",
  keyTakeaways: [
    "Existen opciones sin medicación.",
    "La TMS está autorizada por la FDA para depresión sin respuesta a medicación.",
    "SAINT® es TMS acelerada de cinco días (K220177).",
    "SPRAVATO® está aprobado por la FDA para la depresión resistente.",
  ],
  sections: [
    {
      heading: "Opciones sin medicación",
      paragraphs: [
        "La TMS y SAINT® TMS acelerada usan estimulación magnética, no medicación oral. SAINT® está autorizado por la FDA (K220177) para adultos cuya depresión no ha mejorado con antidepresivos. SPRAVATO® actúa mediante receptores NMDA, un mecanismo distinto.",
      ],
    },
    {
      heading: "El siguiente paso",
      paragraphs: [
        "Una evaluación psiquiátrica puede determinar si estas opciones son apropiadas para usted. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esOptionsIfTherapyDoesntHelp = makeEsArticle({
  slug: "options-if-therapy-doesnt-help-depression",
  title: "Opciones si la terapia no ayuda a la depresión | Neuro Wellness",
  description:
    "Qué hacer si la psicoterapia por sí sola no alivia la depresión: medicación, TMS, SAINT® y SPRAVATO® como opciones complementarias.",
  h1: "Opciones si la terapia no ayuda a la depresión",
  breadcrumbLabel: "Opciones si la terapia no ayuda",
  shortAnswer:
    "La psicoterapia es un tratamiento de primera línea para la depresión, pero por sí sola no siempre es suficiente. Si la terapia no ha producido alivio suficiente, las opciones incluyen antidepresivos, TMS, SAINT® TMS acelerada y SPRAVATO®. La depresión resistente al tratamiento se define como la falta de respuesta a al menos dos antidepresivos en dosis y duración adecuadas. Una evaluación psiquiátrica puede identificar los siguientes pasos.",
  keyTakeaways: [
    "La terapia por sí sola no siempre es suficiente.",
    "Las opciones incluyen antidepresivos, TMS, SAINT® y SPRAVATO®.",
    "La depresión resistente al tratamiento = falta de respuesta a ≥2 antidepresivos.",
    "Una evaluación psiquiátrica identifica los siguientes pasos.",
  ],
  sections: [
    {
      heading: "Cuando la terapia no es suficiente",
      paragraphs: [
        "La psicoterapia es eficaz para muchos, pero la depresión a veces requiere más que terapia. Combinar terapia con medicación o tratamientos sin medicación puede ayudar.",
      ],
    },
    {
      heading: "Opciones más allá de la terapia",
      paragraphs: [
        "La TMS está autorizada por la FDA para la depresión que no ha respondido a medicación. SAINT® es un protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177). SPRAVATO® está aprobado por la FDA para la depresión resistente al tratamiento. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esDepressionImpossibleToTreat = makeEsArticle({
  slug: "depression-feels-impossible-to-treat",
  title: "La depresión parece imposible de tratar | Neuro Wellness",
  description:
    "Si la depresión parece imposible de tratar, existen opciones más allá de los antidepresivos: TMS, SAINT® y SPRAVATO® para la depresión resistente al tratamiento.",
  h1: "La depresión parece imposible de tratar",
  breadcrumbLabel: "La depresión parece imposible",
  shortAnswer:
    "Si la depresión parece imposible de tratar, puede tener depresión resistente al tratamiento, que afecta a aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor. No es imposible: existen opciones más allá de los antidepresivos estándar —TMS, SAINT® TMS acelerada y SPRAVATO®— que actúan por mecanismos distintos. Una evaluación psiquiátrica puede identificar los siguientes pasos.",
  keyTakeaways: [
    "La depresión resistente al tratamiento afecta a ~1/3 de los tratados.",
    "No es imposible de tratar.",
    "TMS, SAINT® y SPRAVATO® son opciones más allá de los antidepresivos.",
    "Una evaluación psiquiátrica identifica los siguientes pasos.",
  ],
  sections: [
    {
      heading: "No es imposible",
      paragraphs: [
        "Aproximadamente un tercio de las personas tratadas por trastorno depresivo mayor no alcanzan la remisión tras dos pasos de tratamiento, según el estudio STAR*D. Es común y tratable con opciones más allá de los antidepresivos estándar.",
      ],
    },
    {
      heading: "Opciones que actúan de forma distinta",
      paragraphs: [
        "La TMS, SAINT® TMS acelerada y SPRAVATO® actúan por mecanismos distintos a los antidepresivos orales. SAINT® está autorizado por la FDA (K220177) para adultos cuya depresión no ha mejorado con antidepresivos. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
